import type { Route } from 'nextjs-routes';

const appRoutes = {
  INDEX: '/',
  PRIVACY: '/privacy',
  TRADE_HISTORY: '/history/[[...tab]]',
  TX: '/tx/[chainId]/[txHash]',
  TOKEN_PROFILE: '/tokens/[chainId]/[...contractAddresses]',
  CHAIN_LANDING: '/tokens/[chainId]',
  TOKENS: '/tokens',
  CROSS_CHAIN_SWAPS: '/cross-chain-swaps',
  SWAP_TOKENS: '/swap-tokens',
  LIMIT_ORDERS: '/limit-orders',
  ABOUT_MATCHA: '/about-matcha-dex-aggregator',
  LIQUIDITY_SOURCES: '/liquidity',
  MATCHA_AUTO: '/matcha-auto',
  NEWSLETTER_SIGNUP: '/newsletter-signup',
  PERPS: '/perps/[[...coin]]',
  PORTFOLIO: '/portfolio',
  TERMS: '/terms',
  TRADE: '/trade',
  SWAP: '/swap',
  ERROR_404: '/404',
} as const satisfies Record<string, Route['pathname']>;

const apiRoutes = {
  EMAIL: '/api/email',
  GOOGLE_TOKEN: '/api/google-token',
  CHART_HISTORY: '/api/charts/get-history',
  USD_PRICES: '/api/price/usd',
  FX_RATES: '/api/price/fx-rates',
  TOKEN_FEE_CONFIG: '/api/tokens/fees',
  TRENDING_TOKENS: '/api/tokens/trending',
  TOKENS_CONTENT: '/api/tokens/content',
  TOKENS_INFO: '/api/tokens/info',
  TRADE_HISTORY: '/api/trade-history',
  TRADE_HISTORY_ROUTE: '/api/trade-history/route',
  TRADE_HISTORY_EXPORT: '/api/trade-history/export',
  LIMIT_ORDER_BUILD: '/api/orderbook/build-order',
  LIMIT_ORDER: '/api/orderbook/order',
  LIMIT_ORDER_CANCEL: '/api/orderbook/cancel',
  LIMIT_ORDERS_INVALIDATE: '/api/orderbook/invalidate',
  LIMIT_ORDERS: '/api/orderbook/orders',
  REFERRAL_INFO: '/api/refer/info',
  SWAP_PRICE: '/api/swap/price',
  SWAP_QUOTE: '/api/swap/quote',
  TRADE_SUBMIT: '/api/trade/submit',
  SOURCES: '/api/sources',
  SOURCES_ALL: '/api/sources/all',
  SEARCH_TOKENS: '/api/tokens/search',
  TOKENS_BY_ADDRESS: '/api/tokens/by-address',
  TOKENS_DEDAUB_SECURITY: '/api/tokens/dedaub-security',
  INTENTS_PRICE: '/api/intents/price',
  INTENTS_QUOTE: '/api/intents/quote',
  INTENTS_SUBMIT: '/api/intents/submit',
  INTENTS_STATUS: '/api/intents/status',
  CROSS_CHAIN_QUOTE: '/api/cross-chain/quote',
  CROSS_CHAIN_STATUS: '/api/cross-chain/status',
  CROSS_CHAIN_HISTORY: '/api/cross-chain/history',
  GAS_PRICE: '/api/gas',
  QUOTE_JWT: '/api/jwt',
  QUOTE_JWT_CHALLENGE: '/api/jwt/challenge',
  SOLANA_SWAP_QUOTE: '/api/swap/quote/solana',
  BALANCES: '/api/balances',
  WALLET_PORTFOLIO: '/api/wallets/portfolio',
  WALLET_POSITIONS: '/api/wallets/positions',
  WALLET_CHART: '/api/wallets/chart',
  WALLET_PNL: '/api/wallets/pnl',
  ZORA: '/api/zora',
  FEEDBACK_SUBMIT: '/api/feedback/submit',
  FEEDBACK_INTERACTION_STATE: '/api/feedback/interaction-state',
  MATCHA_META_ELIGIBILITY: '/api/matcha-meta/eligibility',
  PROMOTION_ELIGIBILITY: '/api/promo-eligibility',
  DISCOVERY_TOP_ZORA_CREATORS: '/api/discovery/top-zora-creators',
  DISCOVERY_TOKEN_ISSUERS: '/api/discovery/token-issuers',
  DISCOVERY_TOKEN_BACKINGS: '/api/discovery/token-backings',
  DISCOVERY_STABLE_CURRENCIES: '/api/discovery/stable-currencies',
  LIQUIDITY_SCORE: '/api/liquidity-score',
  PAIRS: '/api/pairs/[chainId]',
} as const satisfies Record<string, Route['pathname']>;

export const routes = {
  ...appRoutes,
  api: apiRoutes,

  // external resources
  BLOG: 'https://blog.matcha.xyz',
  HELP: 'https://help.matcha.xyz',
  HELP_APPROVE_TOKEN:
    'https://help.matcha.xyz/en/articles/4285134-why-do-i-need-to-approve-my-tokens-before-i-can-trade-them',
  HELP_FEE_ON_TRANSFER: 'https://help.matcha.xyz/en/articles/7239773-what-are-buy-sell-tax-tokens',
  HELP_FAQ: 'https://help.matcha.xyz/collections/7752246105-faq-1',
  HELP_GETTING_STARTED: 'https://help.matcha.xyz/collections/2554751451-getting-started-1',
  BRAND_ASSETS:
    'https://drive.google.com/drive/folders/16z4lhn1h9pm7n12b0hTMwzCknjtZKzaf?usp=sharing',
  HELP_WALLETS: 'https://help.matcha.xyz/en/collections/2438298-browsers-and-wallets',
  HELP_WALLETS_LEARN_MORE: 'https://help.matcha.xyz/en/articles/3952635-what-is-an-ethereum-wallet',
  HELP_WHAT_IS_PRICE_IMPACT: 'https://help.matcha.xyz/en/articles/7251476-what-is-price-impact',
  MATCHA_AUTO_BLOG_POST: 'https://matcha.xyz/matcha-auto',
  ZEROEX_HOME: 'https://www.0x.org/',
  ZEROEX_EXPLORER: 'https://explorer.0xprotocol.org/liquiditySources',
  COINBASE_SMART_WALLET_URL: 'https://wallet.coinbase.com/',
  CODEX_IO: 'https://codex.io/',
  // socials
  TELEGRAM: 'https://t.me/matchaxyz',
  TWITTER: 'https://twitter.com/matchaxyz',
  DISCORD: 'https://discord.com/invite/matchaxyz',
  spindl: {
    EVENTS_SERVER: 'https://spindl.link/events/server',
    SWAP_EVENT: '/api/refer/swap',
  },
} as const;

const marketingPages: string[] = [
  routes.CROSS_CHAIN_SWAPS,
  routes.SWAP_TOKENS,
  routes.LIMIT_ORDERS,
  routes.LIQUIDITY_SOURCES,
  routes.ABOUT_MATCHA,
  routes.MATCHA_AUTO,
  routes.NEWSLETTER_SIGNUP,
];

export const isMarketingPage = (path: string) => marketingPages.includes(path);
